import type { ComponentType } from 'react';
import { Home01, Key01, LogIn01, Mail01, User01, UserCheck01 } from '@untitledui/icons';
import type { User } from '@/types/auth';
import { AuthMethodStep } from '../components/steps/AuthMethodStep';
import { AccountStep } from '../components/steps/AccountStep';
import { VerifyEmailStep } from '../components/steps/VerifyEmailStep';
import { PersonalInfoStep } from '../components/steps/PersonalInfoStep';
import { AddressStep } from '../components/steps/AddressStep';
// import { PaymentStep } from '../components/steps/PaymentStep';
import { ForgotPasswordStep } from '../components/steps/ForgotPasswordStep';
import { ResetPasswordStep } from '../components/steps/ResetPasswordStep';

/**
 * KYC data extends User with flow-specific properties that aren't stored in the database
 */
export interface KycData extends Partial<User> {
  resetToken?: string;
  addressSkipped?: boolean;
}

export interface KycStepProps {
  data: KycData;
  onUpdate: (updates: KycData) => void;
  onComplete: (updates?: KycData) => void;
  onBack?: () => void;
  onActionsChange?: (actions: KycStepActions) => void;
  onNavigateToStep?: (stepId: string) => void;
}

export interface KycStepActions {
  canProceed: boolean;
  onPrimaryClick: () => void | Promise<void>;
  primaryButtonText: string;
  showBack?: boolean;
  isLoading?: boolean;
}

export interface KycStepConfig {
  id: string;
  title: string;
  description: string;
  component: ComponentType<KycStepProps>;
  validation: (data: KycData) => boolean;
  icon: ComponentType<{ className?: string }>;
  allowBack?: boolean;
  canSkip?: boolean;
  standalone?: boolean; // Not part of normal KYC flow progression
}

export const KYC_STEPS: KycStepConfig[] = [
  {
    id: 'auth-method',
    title: 'kyc.steps.authMethod.title',
    description: 'kyc.steps.authMethod.description',
    component: AuthMethodStep,
    validation: (data) => !!data.email,
    icon: LogIn01,
    allowBack: true,
  },
  {
    id: 'account',
    title: 'kyc.steps.account.title',
    description: 'kyc.steps.account.description',
    component: AccountStep,
    validation: (data) => !!data.email && !!data.id,
    icon: User01,
    allowBack: false,
  },
  {
    id: 'personal-info',
    title: 'kyc.steps.personalInfo.title',
    description: 'kyc.steps.personalInfo.description',
    component: PersonalInfoStep,
    validation: (data) => !!data.firstName && !!data.lastName,
    icon: UserCheck01,
    allowBack: false,
  },
  {
    id: 'verify-email',
    title: '',
    description: '',
    component: VerifyEmailStep,
    // Only VERIFIED counts as complete - SKIPPED allows progression but step remains incomplete
    validation: (data) => data.emailVerificationStatus === 'VERIFIED',
    icon: Mail01,
  },
  {
    id: 'address',
    title: 'kyc.steps.address.title',
    description: 'kyc.steps.address.description',
    component: AddressStep,
    // Only actual address data counts as complete - addressSkipped allows progression but step remains incomplete
    validation: (data) => !!data.streetAddress && !!data.city && !!data.zipCode && !!data.country,
    icon: Home01,
    canSkip: true,
  },
  // {
  //   id: 'payment',
  //   title: 'kyc.steps.payment.title',
  //   description: 'kyc.steps.payment.description',
  //   component: PaymentStep,
  //   validation: (data) => !!data.cardLast4,
  //   icon: CreditCard01,
  //   canSkip: true,
  // },
  {
    id: 'forgot-password',
    title: '', // Step manages its own header
    description: '',
    component: ForgotPasswordStep,
    validation: () => false, // Never considered complete in normal flow
    icon: Key01,
    allowBack: true,
    standalone: true, // Not part of normal KYC progression
  },
  {
    id: 'reset-password',
    title: '', // Step manages its own header
    description: '',
    component: ResetPasswordStep,
    validation: () => false, // Never considered complete in normal flow
    icon: Key01,
    allowBack: false,
    standalone: true, // Not part of normal KYC progression
  },
];

export const getActiveSteps = () => KYC_STEPS.filter((step) => !step.standalone);
export const getStepById = (id: string) => KYC_STEPS.find((s) => s.id === id);

export const calculateProgress = (data: KycData) => {
  const activeSteps = getActiveSteps();
  const completedSteps = activeSteps.filter((step) => step.validation(data)).length;
  return Math.round((completedSteps / activeSteps.length) * 100);
};
